/**
 * Cache key construction utilities for the Dashboard framework.
 *
 * The partition key format is `{dashboardId}#{analysisId}` which supports
 * multiple dashboard types sharing the same FinnDashboardCache DynamoDB table.
 */

/**
 * Build a DynamoDB partition key for the dashboard cache.
 *
 * @param dashboardId - Plugin ID, e.g. 'spb-analytics'
 * @param analysisId  - Analysis identifier, e.g. 'finn-analysis+SP+2025-01-01_2025-01-07+2024-01-01_2024-01-07'
 * @returns Composite partition key string
 */
export function buildCacheKey(dashboardId: string, analysisId: string): string {
  return `${dashboardId}#${analysisId}`;
}

/**
 * Build the SPB Analytics analysis ID from filter parameters.
 *
 * Pattern: `finn-analysis+{product}+{tsd}_{ted}+{bsd}_{bed}`
 *
 * @param product       - 'SP' or 'SB'
 * @param targetStart   - Target start date (YYYY-MM-DD)
 * @param targetEnd     - Target end date (YYYY-MM-DD)
 * @param baselineStart - Baseline start date (YYYY-MM-DD)
 * @param baselineEnd   - Baseline end date (YYYY-MM-DD)
 * @returns Analysis ID string
 */
export function buildSPBAnalysisId(
  product: string,
  targetStart: string,
  targetEnd: string,
  baselineStart: string,
  baselineEnd: string
): string {
  return `finn-analysis+${product}+${targetStart}_${targetEnd}+${baselineStart}_${baselineEnd}`;
}

/**
 * Number of days after `targetEndDate` at which we consider the underlying
 * data "settled" — i.e., the daily ETL is no longer rewriting those
 * partitions and the cached payload can be reused indefinitely.
 */
const SETTLED_DAYS = 4;

/**
 * Sentinel TTL for "settled" cache entries. Year ~2096 — effectively
 * forever for our purposes. The DynamoDB schema requires a non-null TTL,
 * so we send this rather than omitting the field. After a manual backfill,
 * the affected rows must be deleted from DynamoDB (see runbook).
 */
const FAR_FUTURE_TTL = 4_000_000_000;

/**
 * Compute the DynamoDB TTL (epoch seconds) for a cache row, based on how
 * recent the target end date is.
 *
 * - Settled (target_end >= SETTLED_DAYS days ago): keep "forever".
 * - In-progress (target_end < SETTLED_DAYS days ago): expire at next UTC
 *   midnight, so the next-day ETL output is picked up automatically.
 */
export function computeCacheTtl(targetEndDate: string): number {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const end = new Date(`${targetEndDate}T00:00:00Z`);
  const daysAgo = Math.floor(
    (today.getTime() - end.getTime()) / (24 * 60 * 60 * 1000)
  );

  if (daysAgo >= SETTLED_DAYS) return FAR_FUTURE_TTL;

  const midnight = new Date();
  midnight.setUTCHours(24, 0, 0, 0);
  return Math.floor(midnight.getTime() / 1000);
}
